import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Flag from 'react-world-flags'
import { ICv, IPersonSkill } from 'types/cvsInterfaces'
import { ISearchFields, ISearchResult } from 'types/searchInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import { Link } from 'react-router'
import { personAPI } from 'api/PersonAPI'
import { IError } from 'types/error'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { CountriesData } from 'api/UtilsAPI'
import { useTranslation } from 'react-i18next'
import { allocationAPI } from 'api/AllocationAPI'
import { IAllocation } from 'types/assignmentInterfaces'
import AllocationTimeline from 'Components/reusable/AllocationTimeline/AllocationTimeline'
import { useOverflow } from 'use-overflow'
import { ISalesPoolItem } from 'types/salesPoolInterfaces'
import { useSalesPool } from 'Components/General/SalesPoolProvider/SalesPoolProvider'
import { useNotificationPopup } from 'Components/reusable/Notification'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import PsychologyIcon from '@mui/icons-material/Psychology'
import { filesAPI } from 'api/FilesAPI'
import { useUser } from 'hooks'
import { teamAPI } from 'api/TeamAPI'

/** @notExported */
interface IResultContentProps {
  /** The search result. */
  result: ISearchResult
  /** The cv. */
  cv: ICv | null | undefined
  /** The countries options. */
  countries: CountriesData
  /** The search terms. */
  terms?: ISearchFields
  /** The function to set the open proposal. */
  setOpenProposal?: (openProposal: string) => void
  /** Whether the action should be disabled. */
  disableAction?: boolean
  /** Whether to show allocations. */
  showAllocations?: boolean
  /** Whether to show the profile image. */
  showProfileImage?: boolean
  /** Whether to show the update notification button */
  showUpdateNotification?: boolean
  /** Whether used in a modal. */
  modal?: boolean
}

/**
 * Component for rendering the result content.
 *
 * @param result - The search result.
 * @param cv - The cv.
 * @param countries - The countries options.
 * @param terms - The search terms.
 * @param setOpenProposal - The function to set the open proposal.
 * @param disableAction - Whether the action should be disabled.
 * @param showAllocations - Whether to show allocations.
 * @returns
 * @notExported
 */
const ResultContent: React.FC<IResultContentProps> = ({
  result,
  cv,
  countries,
  setOpenProposal,
  disableAction,
  showAllocations = true,
  showProfileImage = true,
  showUpdateNotification = false,
  modal = false,
}) => {
  const { t, i18n } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const { refXOverflowing } = useOverflow(ref as unknown as React.RefObject<HTMLElement>)
  const { editedSalesPool, setEditedSalesPool } = useSalesPool()
  const { setNotificationPopup } = useNotificationPopup()
  const { groups, features } = useUser()

  const [image, setImage] = useState<string>('')
  const [allocations, setAllocations] = useState<IAllocation[]>([])
  const [backendError, setBackendError] = useState<IError>()
  const [updateNotification, setUpdateNotification] = useState<boolean>(false)

  useEffect(() => {
    const controller = new AbortController()

    if (result.Person.id && !result.publicId) {
      ;(async () => {
        try {
          let allocationData: IAllocation[] | undefined = undefined
          if (showAllocations) {
            allocationData = await allocationAPI.getAllocationsForTimeline(result.Person.id, controller)
          }
          const data = showProfileImage ? await personAPI.getProfileImage(result.Person.id, controller) : ''
          {
            setImage(data)
            if (showAllocations && allocationData) {
              setAllocations(allocationData)
            }
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    if (result.publicId) {
      ;(async () => {
        try {
          if (result.publicId) {
            const data = await filesAPI.downloadProfilePublicId(result.publicId, controller)
            let allocationData: IAllocation[] | undefined = undefined
            if (showAllocations) {
              allocationData = await allocationAPI.getAllocationsForPublicIdForTimeline(result.publicId, controller)
            }
            {
              setImage(data)
              if (showAllocations && allocationData) {
                setAllocations(allocationData)
              }
            }
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [result.Person, result.publicId, showAllocations, showProfileImage])

  useEffect(() => {
    const controller = new AbortController()

    if (updateNotification) {
      ;(async () => {
        try {
          await teamAPI.sendUpdateNotification(result.Person.id, controller)

          setNotificationPopup({
            message: t('team.updateNotificationSent'),
            type: 'success',
            duration: 'short',
          })
        } catch (error) {
          setBackendError(error as IError)
        } finally {
          setUpdateNotification(false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [updateNotification, result.Person, setNotificationPopup, t])

  const allocationTimeline = useMemo(() => {
    const allocationPersonId = result.publicId ? result.publicId : result.personId
    if (!showAllocations || !allocationPersonId) {
      return null
    }
    if (!allocations || allocations.length < 1) {
      return <AllocationTimeline personId={allocationPersonId} />
    }
    return <AllocationTimeline personId={allocationPersonId} data={allocations} />
  }, [allocations, result, showAllocations])

  const primaryRole = useMemo(() => chooseDBTranslation(i18n, cv)?.primaryRole, [i18n, cv])
  const organizationName = useMemo(
    () => chooseDBTranslation(i18n, result.Person.Site?.Organization)?.name,
    [i18n, result.Person.Site?.Organization]
  )
  const city = result.Person.Site?.city
  const country = result.Person.Site?.countryCode.toUpperCase()

  const sortedArray: IPersonSkill[] = useMemo(() => {
    if (cv?.PersonSkills) {
      const matchedSkillSet = new Set(result.matchedSkills ?? [])
      const similarSkillSet = new Set(result.similarSkills ?? [])

      return [...cv.PersonSkills].sort((a, b) => {
        // Priority: matched skills (0) > similar skills (1) > other skills (2)
        const aPriority = matchedSkillSet.has(a.skillId) ? 0 : similarSkillSet.has(a.skillId) ? 1 : 2
        const bPriority = matchedSkillSet.has(b.skillId) ? 0 : similarSkillSet.has(b.skillId) ? 1 : 2
        return aPriority - bPriority
      })
    } else {
      return []
    }
  }, [cv?.PersonSkills, result.matchedSkills, result.similarSkills])

  const skillChips = useMemo(
    () =>
      sortedArray &&
      sortedArray.map(personSkill => {
        const name = chooseDBTranslation(i18n, personSkill.Skill).name
        const isMatchedSkill = result.matchedSkills?.includes(personSkill.skillId)
        const isSimilarSkill = result.similarSkills?.includes(personSkill.skillId)
        const isHighlighted = isMatchedSkill || isSimilarSkill

        const chip = (
          <Chip
            key={personSkill.id}
            size="small"
            label={name}
            color={isMatchedSkill ? 'primary' : isSimilarSkill ? 'secondary' : undefined}
            variant={isHighlighted ? 'filled' : 'outlined'}
            sx={{ mr: 0.5, borderRadius: 0.5 }}
          />
        )

        // Show tooltip for similar skills
        if (isSimilarSkill) {
          return (
            <Tooltip key={personSkill.id} title={t('search.similarSkillMatch')}>
              {chip}
            </Tooltip>
          )
        }

        return chip
      }),
    [i18n, result.matchedSkills, result.similarSkills, sortedArray, t]
  )

  const sortedCertificates = useMemo(() => {
    if (cv?.PersonCertificates) {
      const matchedCertificateSet = new Set(result.matchedCertificates ?? [])
      const similarCertificateSet = new Set(result.similarCertificates ?? [])

      return [...cv.PersonCertificates].sort((a, b) => {
        // Priority: matched certificates (0) > similar certificates (1) > other certificates (2)
        const aPriority = matchedCertificateSet.has(a.certificateId)
          ? 0
          : similarCertificateSet.has(a.certificateId)
            ? 1
            : 2
        const bPriority = matchedCertificateSet.has(b.certificateId)
          ? 0
          : similarCertificateSet.has(b.certificateId)
            ? 1
            : 2
        return aPriority - bPriority
      })
    } else {
      return []
    }
  }, [cv?.PersonCertificates, result.matchedCertificates, result.similarCertificates])

  const certificateChips = useMemo(
    () =>
      sortedCertificates &&
      sortedCertificates.map(certificate => {
        const name = chooseDBTranslation(i18n, certificate.Certificate)?.certificate
        const isMatchedCertificate = result.matchedCertificates?.includes(certificate.certificateId)
        const isSimilarCertificate = result.similarCertificates?.includes(certificate.certificateId)
        const isHighlighted = isMatchedCertificate || isSimilarCertificate

        const chip = (
          <Chip
            key={certificate.id}
            size="small"
            label={name}
            color={isMatchedCertificate ? 'primary' : isSimilarCertificate ? 'secondary' : undefined}
            variant={isHighlighted ? 'filled' : 'outlined'}
            sx={{ mr: 0.5, borderRadius: 0.5 }}
          />
        )

        // Show tooltip for similar certificates
        if (isSimilarCertificate) {
          return (
            <Tooltip key={certificate.id} title={t('search.similarCertificateMatch')}>
              {chip}
            </Tooltip>
          )
        }

        return chip
      }),
    [i18n, result.matchedCertificates, result.similarCertificates, sortedCertificates, t]
  )

  const sortedCompetences = useMemo(() => {
    if (cv?.PersonCompetences && features.includes('competences')) {
      const matchedCompetenceSet = new Set(result.matchedCompetences ?? [])
      const similarCompetenceSet = new Set(result.similarCompetences ?? [])

      return [...cv.PersonCompetences].sort((a, b) => {
        // Priority: matched competences (0) > similar competences (1) > other competences (2)
        const aPriority = matchedCompetenceSet.has(a.competenceId)
          ? 0
          : similarCompetenceSet.has(a.competenceId)
            ? 1
            : 2
        const bPriority = matchedCompetenceSet.has(b.competenceId)
          ? 0
          : similarCompetenceSet.has(b.competenceId)
            ? 1
            : 2
        return aPriority - bPriority
      })
    } else {
      return []
    }
  }, [cv?.PersonCompetences, features, result.matchedCompetences, result.similarCompetences])

  const competenceChips = useMemo(() => {
    if (!features.includes('competences')) {
      return
    }
    return (
      sortedCompetences &&
      sortedCompetences.map(competence => {
        const name = chooseDBTranslation(i18n, competence.Competence)?.name
        const isMatchedCompetence = result.matchedCompetences?.includes(competence.competenceId)
        const isSimilarCompetence = result.similarCompetences?.includes(competence.competenceId)
        const isHighlighted = isMatchedCompetence || isSimilarCompetence

        const chip = (
          <Chip
            key={competence.id}
            size="small"
            label={name}
            color={isMatchedCompetence ? 'primary' : isSimilarCompetence ? 'secondary' : undefined}
            variant={isHighlighted ? 'filled' : 'outlined'}
            sx={{ mr: 0.5, borderRadius: 0.5 }}
          />
        )

        // Show tooltip for similar competences
        if (isSimilarCompetence) {
          return (
            <Tooltip key={competence.id} title={t('search.similarCompetenceMatch')}>
              {chip}
            </Tooltip>
          )
        }

        return chip
      })
    )
  }, [features, i18n, result.matchedCompetences, result.similarCompetences, sortedCompetences, t])

  const LinkBehavior = (props: Omit<React.ComponentProps<typeof Link>, 'to'>) => (
    <Link
      to={`/profile/${result.personId}`}
      state={{
        search: cv ? cv.id : null,
        personId: cv ? cv.personId : null,
      }}
      {...props}
      role={undefined}
    />
  )

  /**
   * Generates a list of skills.
   *
   * @param {boolean} isAnonymous - indicates if the user is anonymous
   * @return {React.ReactElement} the list of skills as a JSX element
   */
  const generateSkillList = (isAnonymous?: boolean): React.ReactElement => (
    <Grid size={12} container wrap="nowrap" justifyContent="space-between" my={0.5}>
      <Grid
        ref={ref}
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          WebkitMaskImage: refXOverflowing ? 'linear-gradient(90deg, #000 70%, transparent)' : undefined,
        }}
        size={{
          xs: 8,
          md: 9,
          lg: 10,
        }}
      >
        {skillChips}
      </Grid>
      <Grid>
        {!disableAction && (
          <>
            {isAnonymous && result.anonymousId ? (
              <Button
                data-testid={`button-result-content-propose-assignment-${result.anonymousId}`}
                variant="contained"
                color="newPrimary"
                size="small"
                onClick={() => {
                  if (result.anonymousId && !!setOpenProposal) setOpenProposal(result.anonymousId)
                }}
              >
                {t('search.proposeAssignment')}
              </Button>
            ) : (
              <Button
                data-testid={`button-result-content-full-profile-${result.Person.id}`}
                variant="contained"
                color="newPrimary"
                size="small"
                component={LinkBehavior}
              >
                {t('search.fullProfile')}
              </Button>
            )}
          </>
        )}
      </Grid>
    </Grid>
  )

  /**
   * Renders a list of certificates.
   *
   * @return {React.ReactElement} The rendered certificate list component
   */
  const generateCertificateList = () => (
    <Grid container alignItems="center" size={12}>
      <Tooltip title={t('search.certificate')}>
        <SvgIcon sx={{ p: 0.5 }}>
          <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
            <path d="M458.622 255.92l45.985-45.005c13.708-12.977 7.316-36.039-10.664-40.339l-62.65-15.99 17.661-62.015c4.991-17.838-11.829-34.663-29.661-29.671l-61.994 17.667-15.984-62.671C337.085.197 313.765-6.276 300.99 7.228L256 53.57 211.011 7.229c-12.63-13.351-36.047-7.234-40.325 10.668l-15.984 62.671-61.995-17.667C74.87 57.907 58.056 74.738 63.046 92.572l17.661 62.015-62.65 15.99C.069 174.878-6.31 197.944 7.392 210.915l45.985 45.005-45.985 45.004c-13.708 12.977-7.316 36.039 10.664 40.339l62.65 15.99-17.661 62.015c-4.991 17.838 11.829 34.663 29.661 29.671l61.994-17.667 15.984 62.671c4.439 18.575 27.696 24.018 40.325 10.668L256 458.61l44.989 46.001c12.5 13.488 35.987 7.486 40.325-10.668l15.984-62.671 61.994 17.667c17.836 4.994 34.651-11.837 29.661-29.671l-17.661-62.015 62.65-15.99c17.987-4.302 24.366-27.367 10.664-40.339l-45.984-45.004z"></path>
          </svg>
        </SvgIcon>
      </Tooltip>
      {certificateChips}
    </Grid>
  )

  /**
   * Renders a list of competences.
   *
   * @return {React.ReactElement} The rendered competences list component
   */
  const generateCompetenceList = () => {
    if (features.includes('competences'))
      return (
        <Grid container alignItems="center" size={12}>
          <Tooltip title={t('search.competence')}>
            <PsychologyIcon color="primary" />
          </Tooltip>
          {competenceChips}
        </Grid>
      )
  }

  /**
   * Generates a list of language flags based on the language skills in the CV.
   *
   * @return {React.ReactElement} The list of language flags as JSX elements.
   */
  const generateLanguageList = () => {
    if (cv && cv.LanguageSkills) {
      return cv.LanguageSkills.map(languageSkill => {
        return (
          <Flag
            key={languageSkill.id}
            alt={t(`languages.${languageSkill.languageCode}`)}
            style={{
              height: '30px',
              width: '32px',
              margin: 5,
            }}
            code={
              languageSkill.languageCode === 'en'
                ? 'gb'
                : languageSkill.languageCode === 'sv'
                  ? 'se'
                  : languageSkill.languageCode
            }
            fallback={<span></span>}
          />
        )
      })
    }

    return null
  }

  const addToPool = () => {
    if ('id' in result.Person) {
      const newItem = {
        personId: result.Person.id,
        Person: result.Person,
      } as ISalesPoolItem

      if (
        editedSalesPool &&
        editedSalesPool.SalesPoolItems &&
        editedSalesPool.SalesPoolItems.length > 0 &&
        editedSalesPool.SalesPoolItems.find(item => item.personId === newItem.personId)
      ) {
        setNotificationPopup({
          message: t('salesPool.itemExists'),
          type: 'warning',
          duration: 'short',
        })
      } else {
        if (editedSalesPool && editedSalesPool.SalesPoolItems) {
          setEditedSalesPool({
            ...editedSalesPool,
            SalesPoolItems: [...editedSalesPool.SalesPoolItems, newItem],
          })
        } else {
          setEditedSalesPool({
            title: '',
            anonymous: false,
            durationInWeeks: 4,
            allocationsVisible: false,
            publicId: undefined,
            SalesPoolItems: [newItem],
          })
        }

        setNotificationPopup({
          message: t('salesPool.itemAdded'),
          type: 'success',
          duration: 'short',
        })
      }
    }
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (result.anonymous) {
    return (
      <Grid container spacing={1}>
        <Avatar
          alt={t('resources.searchResults.anonymousProfile')}
          sx={{
            height: 70,
            width: 70,
            mt: 1,
            ml: 1,
          }}
        />
        <Grid
          size={{
            xs: 8,
            lg: 9,
          }}
        >
          <Grid size={12}>{`${primaryRole?.length ? primaryRole : t('resources.searchResults.missingRole')}`}</Grid>
          {country && country?.length > 1 && <Grid size={12}>{`${country ? countries[country] : ''}`}</Grid>}
        </Grid>
        <Grid
          justifySelf="flex-end"
          sx={{ textAlign: 'right' }}
          size={{
            xs: 1,
            md: 1,
          }}
        >
          {generateLanguageList()}
        </Grid>
        {generateSkillList(true)}
        {certificateChips && certificateChips?.length > 0 && generateCertificateList()}
        {features.includes('competences') && competenceChips && competenceChips?.length > 0 && generateCompetenceList()}
      </Grid>
    )
  }

  return (
    <Grid container spacing={1}>
      <Grid
        size={{
          xs: 12,
          sm: modal ? 2 : 2,
          md: modal ? 3 : 1.5,
          lg: modal ? 2 : 1,
        }}
      >
        <Avatar
          alt={`${result.Person?.firstName ?? ''} ${result.Person?.lastName ?? ''}`.trim()}
          sx={{
            height: 70,
            width: 70,
            marginRight: 5,
          }}
          src={image}
        />
      </Grid>
      <Grid
        container
        size={{
          xs: 12,
          sm: modal ? 8 : 7,
          md: modal ? 7 : 8,
          lg: modal ? 7 : 8,
        }}
      >
        <Grid size={12}>
          <Typography fontWeight="bold">{`${result.Person.firstName} ${result.Person.lastName}`}</Typography>
        </Grid>
        {((primaryRole && primaryRole?.length > 1) || (organizationName && organizationName?.length > 1)) && (
          <Grid size={12}>
            {`${primaryRole?.length ? primaryRole : t('resources.searchResults.missingRole')}${
              organizationName?.length ? `, ${organizationName}` : ''
            }`}
          </Grid>
        )}
        {((city && city?.length > 1) || (country && country?.length > 1)) && (
          <Grid size={12}>{`${city ? `${city}, ` : ''}${country ? countries[country] : ''}`}</Grid>
        )}
      </Grid>
      <Grid
        justifySelf="flex-end"
        container
        alignItems="center"
        justifyContent="flex-end"
        sx={{ textAlign: 'right' }}
        spacing={3}
        size="grow"
      >
        {cv && cv.LanguageSkills && cv.LanguageSkills.length > 0 && <Grid>{generateLanguageList()}</Grid>}
        {!disableAction && groups.includes('sales') && (
          <Grid>
            <Button
              data-testid={`button-result-content-add-to-pool-${result.Person.id}`}
              aria-label={t('profile.addToPool')}
              variant="outlined"
              color="primary"
              size="small"
              onClick={e => {
                e.stopPropagation()
                addToPool()
              }}
            >
              <GroupAddIcon />
            </Button>
          </Grid>
        )}
      </Grid>
      {generateSkillList()}
      {certificateChips && certificateChips?.length > 0 && generateCertificateList()}
      {features.includes('competences') && competenceChips && competenceChips?.length > 0 && generateCompetenceList()}
      {showAllocations && <Grid size={12}>{allocationTimeline}</Grid>}
      {showUpdateNotification && !updateNotification && (
        <Grid>
          <Button
            data-testid={`button-result-content-update-notification-${result.Person.id}`}
            variant="contained"
            color="primary"
            size="small"
            onClick={e => {
              e.stopPropagation()
              setUpdateNotification(true)
            }}
          >
            {t('team.updateProfileNotificationButton')}
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default ResultContent
